import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import clinicbookingService from '../../../../../services/clinicbooking.service';

function AddEditDoctorInfo({ doctor, updateDoc, toogleEdit }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { clinicId } = useParams();

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: doctor?.id ? doctor : {},
    });

    useEffect(() => {
        if (doctor?.id) {
            setValue('userId', doctor.userId);
            setValue('name', doctor.name);
            setValue('photoUrl', doctor.photoUrl || '');
            setValue('isActive', doctor.isActive ? 'true' : 'false');
            setValue('bio', doctor.bio || '');
        }
    }, [doctor, setValue]);

    const onSubmit = (data) => {
        setIsSubmitting(true);

        const updateData = {
            ...data,
            isActive: data.isActive === 'true',
            id: doctor.id || undefined,
        };

        delete updateData.createdAt;
        delete updateData.modifiedAt;
        delete updateData.apptTypes;

        clinicbookingService
            .addUpdateDoctor(clinicId, updateData)
            .then((res) => {
                toast.success(doctor.id ? 'Doctor updated successfully!' : 'Doctor added successfully!', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });

                if (updateDoc) {
                    const updatedDoctor = res.data || {
                        ...doctor,
                        ...updateData,
                        modifiedAt: new Date().toLocaleString(),
                    };
                    updateDoc(updatedDoctor);
                }

                if (toogleEdit) toogleEdit();
            })
            .catch((err) => {
                console.log(err);
                toast.error(err.response?.data?.message || 'Failed to save doctor information', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className="notifications-panel">
            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-person-badge"></i>
                    {doctor?.id ? 'Edit Doctor Information' : 'Add New Doctor'}
                </span>
            </div>
            <div className="notifications-panel__body">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label htmlFor="edit-doctor-name" className="notifications-form-label">
                                Display Name <span className="text-danger">*</span>
                            </label>
                            <input
                                id="edit-doctor-name"
                                type="text"
                                className={`form-control form-control-sm ${errors.name ? 'is-invalid' : ''}`}
                                {...register('name', {
                                    required: 'Display name is required',
                                    minLength: {
                                        value: 2,
                                        message: 'Display name must be at least 2 characters',
                                    },
                                })}
                                placeholder="Enter display name as shown to patients"
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>

                        <div className="col-md-6">
                            <label htmlFor="edit-doctor-photo" className="notifications-form-label">
                                Photo URL
                            </label>
                            <input
                                id="edit-doctor-photo"
                                type="url"
                                className="form-control form-control-sm"
                                {...register('photoUrl')}
                                placeholder="https://example.com/photo.jpg"
                            />
                            <div className="notifications-form-hint">Leave blank to use default avatar.</div>
                        </div>

                        <div className="col-md-6">
                            <label htmlFor="edit-doctor-status" className="notifications-form-label">
                                Status <span className="text-danger">*</span>
                            </label>
                            <select
                                id="edit-doctor-status"
                                className={`form-select form-select-sm ${errors.isActive ? 'is-invalid' : ''}`}
                                {...register('isActive', { required: 'Status is required' })}
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                            {errors.isActive && <div className="invalid-feedback">{errors.isActive.message}</div>}
                        </div>

                        <div className="col-12">
                            <label htmlFor="edit-doctor-bio" className="notifications-form-label">
                                Biography
                            </label>
                            <textarea
                                id="edit-doctor-bio"
                                className="form-control form-control-sm"
                                rows={5}
                                {...register('bio')}
                                placeholder="Enter doctor's biography, qualifications, specialties, and experience..."
                            ></textarea>
                            <div className="notifications-form-hint">Visible to patients when booking appointments.</div>
                        </div>
                    </div>

                    {doctor?.id && (
                        <div className="clinic-booking-doctor-system-info">
                            <div className="clinic-booking-doctor-section__title">System Information</div>
                            <div className="clinic-booking-doctor-system-info__grid">
                                <div>
                                    <span className="notifications-info-label">Created</span>
                                    <span className="notifications-info-value">{doctor.createdAt || '—'}</span>
                                </div>
                                <div>
                                    <span className="notifications-info-label">Last Modified</span>
                                    <span className="notifications-info-value">{doctor.modifiedAt || '—'}</span>
                                </div>
                                <div>
                                    <span className="notifications-info-label">User ID</span>
                                    <span className="notifications-info-value font-monospace small">{doctor.userId || '—'}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="clinic-booking-form-actions">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={toogleEdit} disabled={isSubmitting}>
                            <i className="bi bi-x-lg me-1"></i>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-success btn-sm" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                    Saving…
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-floppy me-1"></i>
                                    {doctor?.id ? 'Save Changes' : 'Add Doctor'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddEditDoctorInfo;
